/**
 * Builds the MUI v7 theme from the legacy v4 theme config in `./theme`.
 * Converts the v4 `overrides`/`props` shape to v7 `components`.
 */
import { createTheme, type ThemeOptions } from '@mui/material/styles';
import { baseTheme, altTheme } from './theme';
import { isHRS } from './constants/siteConfig';

function toV7(config: any): ThemeOptions {
  const { overrides, props, ...rest } = config ?? {};
  const components: Record<string, any> = {};

  if (overrides) {
    for (const [comp, value] of Object.entries<any>(overrides)) {
      if (comp === 'MuiCssBaseline' && value?.['@global']) {
        components.MuiCssBaseline = { styleOverrides: value['@global'] };
      } else {
        components[comp] = { styleOverrides: value };
      }
    }
  }
  if (props) {
    for (const [comp, defaultProps] of Object.entries<any>(props)) {
      components[comp] = { ...(components[comp] ?? {}), defaultProps };
    }
  }

  // MUI v5+ changed <Link>'s default `underline` from "hover" to "always".
  // Restore the v4 default globally so links only underline on hover.
  components.MuiLink = {
    ...(components.MuiLink ?? {}),
    defaultProps: { underline: 'hover', ...(components.MuiLink?.defaultProps ?? {}) },
  };

  // MUI v5+ changed the default `variant` from "standard" (v4) to "outlined".
  // Restore "standard" on TextField/FormControl so the ported forms keep their
  // original underline styling. NOTE: deliberately NOT setting it on MuiSelect —
  // a defaultProps variant on Select overrides the variant it would otherwise
  // inherit from a wrapping <FormControl variant="...">, which broke outlined
  // selects (e.g. the Priority filter). Selects inherit from their FormControl.
  //
  // Also default `size: 'small'`. In MUI v4 the ported app got its compact field
  // height from `margin="dense"`, which no longer compacts height in v5+ (only
  // `size="small"` does). Setting it here makes every field/select dense by
  // default — matching the original look — without touching each call site.
  // FormControl propagates size via context to its Select/InputLabel; TextField
  // covers standalone fields. Any field wanting full height can pass size="medium".
  for (const comp of ['MuiTextField', 'MuiFormControl'] as const) {
    components[comp] = {
      ...(components[comp] ?? {}),
      defaultProps: { variant: 'standard', size: 'small', ...(components[comp]?.defaultProps ?? {}) },
    };
  }

  // MUI v5+ zeroes DialogContent's padding-top when it directly follows a
  // DialogTitle. Combined with `overflow-y: auto`, that clips the top half of an
  // outlined field's floating label at the top of a titled dialog. Restore the
  // default top padding so labels aren't cut off (affects every titled dialog).
  components.MuiDialogContent = {
    ...(components.MuiDialogContent ?? {}),
    styleOverrides: {
      ...(components.MuiDialogContent?.styleOverrides ?? {}),
      root: {
        '.MuiDialogTitle-root + &': { paddingTop: '20px' },
      },
    },
  };

  // MUI v5+ changed the default breakpoint values (md 960->900, lg 1280->1200,
  // xl 1920->1536). Restore the v4 values so every responsive layout (Grid
  // breakpoints, useMediaQuery, etc.) behaves exactly as it did in the CRA app.
  const breakpoints = {
    values: { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920 },
    ...(rest.breakpoints ?? {}),
  };

  return { ...rest, breakpoints, components } as ThemeOptions;
}

export const theme = createTheme(toV7(isHRS ? altTheme : baseTheme));

export default theme;
